import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle2, Eye, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { friendlyError } from "@/lib/friendlyError";
import { OnboardingPortalView } from "@/pages/InstructorOnboarding";

interface Props {
  instructorId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the review status changes so the parent can refresh. */
  onReviewChanged?: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  ready_for_review: "Ready for review",
  approved: "Approved",
};

export default function OnboardingPacketPreview({
  instructorId, open, onOpenChange, onReviewChanged,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [tab, setTab] = useState<"email" | "portal">("email");


  const load = useCallback(async (action: "preview" | "mark_ready" | "approve" | "reopen" = "preview") => {
    if (action === "preview") setLoading(true); else setBusy(true);
    const { data, error } = await supabase.functions.invoke("admin-onboarding-preview", {
      body: { instructorId, action },
    });
    setLoading(false);
    setBusy(false);
    if (error || (data as any)?.error) {
      friendlyError({ what: "I couldn't build the onboarding preview.", technical: error ?? (data as any)?.error });
      return;
    }
    setPreview(data);
    if (action !== "preview") {
      toast.success(action === "approve" ? "Packet approved — sending is now enabled." :
        action === "mark_ready" ? "Marked ready for review." : "Approval cleared — review again before sending.");
      onReviewChanged?.();
    }
  }, [instructorId, onReviewChanged]);

  useEffect(() => { if (open) load("preview"); }, [open, load]);

  // The email HTML is rendered inside a locked-down iframe, so its anchors can
  // never navigate. We inject a tiny click bridge (scripts only — no
  // same-origin access) so the admin can *test* the button: clicking it jumps
  // to the Instructor Portal Preview instead of following the placeholder URL.
  useEffect(() => {
    if (!open) return;
    const onMessage = (e: MessageEvent) => {
      if (e.data?.source !== "ss-onboarding-email-preview") return;
      setTab("portal");
      toast.info("Preview only — this opens the Instructor Portal Preview. No onboarding token was created.");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open]);

  const emailSrcDoc = preview?.email?.html
    ? `${preview.email.html}
<script>
document.addEventListener('click', function (e) {
  var a = e.target && e.target.closest ? e.target.closest('a') : null;
  if (!a) return;
  e.preventDefault();
  parent.postMessage({ source: 'ss-onboarding-email-preview', href: a.getAttribute('href') || '' }, '*');
}, true);
document.querySelectorAll('a').forEach(function (a) { a.style.cursor = 'pointer'; });
<\/script>`
    : "";

  const status = preview?.reviewStatus ?? "draft";



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4" /> Onboarding Packet Preview
            <Badge variant={status === "approved" ? "default" : "secondary"}>
              {STATUS_LABEL[status] ?? status}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Rendered from the same templates the real onboarding flow uses. Nothing here
            sends email, mints a secure link, or saves instructor data.
          </DialogDescription>
        </DialogHeader>

        {loading || !preview ? (
          <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Building preview…
          </div>
        ) : (
          <div className="space-y-4">
            {preview.contentChangedSinceApproval && (
              <Alert>
                <AlertTitle>Packet contents changed since approval</AlertTitle>
                <AlertDescription>
                  What this instructor still needs has changed. Review and approve again before sending.
                </AlertDescription>
              </Alert>
            )}

            <div className="rounded border p-3 text-sm space-y-2">
              <div className="text-xs text-muted-foreground">
                Recipient: {preview.email?.recipientMasked} · Class: {preview.className}
                {preview.sentAt && <> · Last sent {new Date(preview.sentAt).toLocaleString()}</>}
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <div className="font-medium text-xs mb-1">Will be requested from this instructor</div>
                  {preview.requirements?.outstanding?.length ? (
                    <ul className="list-disc pl-5 text-xs space-y-0.5">
                      {preview.requirements.outstanding.map((i: string) => <li key={i}>{i}</li>)}
                    </ul>
                  ) : <div className="text-xs text-muted-foreground">Nothing outstanding.</div>}
                </div>
                <div>
                  <div className="font-medium text-xs mb-1">Already on file — will not be requested</div>
                  {preview.requirements?.completed?.length ? (
                    <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
                      {preview.requirements.completed.map((i: string) => <li key={i}>{i}</li>)}
                    </ul>
                  ) : <div className="text-xs text-muted-foreground">None yet.</div>}
                </div>
              </div>
            </div>

            <Tabs value={tab} onValueChange={(v) => setTab(v as "email" | "portal")}>
              <TabsList>
                <TabsTrigger value="email">Email Preview</TabsTrigger>
                <TabsTrigger value="portal">Instructor Portal Preview</TabsTrigger>
              </TabsList>

              <TabsContent value="email" className="space-y-2">
                <div className="text-sm">
                  <span className="text-muted-foreground">Subject: </span>
                  <span className="font-medium">{preview.email?.subject}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Links here are inert placeholders. Clicking “Complete Your Onboarding”
                  opens the Instructor Portal Preview — no real token is created or used.
                </p>
                <iframe
                  title="Onboarding email preview"
                  srcDoc={emailSrcDoc}
                  sandbox="allow-scripts"
                  className="w-full h-[600px] border rounded bg-white"
                />
                <div>
                  <Button variant="outline" size="sm" onClick={() => setTab("portal")}>
                    Open Instructor Portal Preview
                  </Button>
                </div>
              </TabsContent>


              <TabsContent value="portal">
                <div className="border rounded overflow-hidden">
                  <OnboardingPortalView
                    state={preview.portalState}
                    call={async () => null}
                    readOnly
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex items-center justify-end gap-2 border-t pt-3">
              {status !== "approved" && (
                <Button variant="outline" size="sm" disabled={busy}
                  onClick={() => load("mark_ready")}>
                  Mark Ready for Review
                </Button>
              )}
              {status === "approved" && !preview.contentChangedSinceApproval ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-700 flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" /> Approved — sending enabled
                  </span>
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => load("reopen")}>
                    Withdraw Approval
                  </Button>
                </div>
              ) : (
                <Button size="sm" disabled={busy} onClick={() => load("approve")}>
                  <ShieldCheck className="h-4 w-4 mr-2" /> Approve Packet
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
